import React from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useTheme } from "../context/ThemeContext";

const AccessibilityPolicy: React.FC = () => {
  const { isDarkMode } = useTheme();

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <Navbar />
      
      <div className="flex-1 py-8 sm:py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Accessibility Policy
            </h1>
            <p className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className={`rounded-lg shadow-md p-6 sm:p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="prose max-w-none">
              <section className="mb-6 sm:mb-8">
                <h2 
                  className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Our Commitment
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all of our users.
                </p>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 
                  className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Accessibility Standards
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Our platform aims to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at the AA level. These guidelines explain how to make web content more accessible for people with disabilities and user-friendly for everyone.
                </p>
                <p className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  We strive to ensure our platform is accessible to users with various disabilities including:
                </p>
                <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 leading-relaxed space-y-2 ml-4 mt-4">
                  <li>Visual impairments</li>
                  <li>Hearing impairments</li>
                  <li>Motor impairments</li>
                  <li>Cognitive disabilities</li>
                </ul>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 
                  className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Accessibility Features
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  We have implemented various features to improve accessibility:
                </p>
                <ul className={`list-disc list-inside text-sm sm:text-base leading-relaxed space-y-2 ml-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>Semantic HTML structure for screen readers</li>
                  <li>Keyboard navigation support</li>
                  <li>High contrast color schemes</li>
                  <li>Scalable text and responsive design</li>
                  <li>Alternative text for images</li>
                  <li>Clear and consistent navigation</li>
                  <li>Form labels and error messages</li>
                  <li>Focus indicators for interactive elements</li>
                </ul>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 
                  className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Assistive Technologies
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Our platform is designed to be compatible with assistive technologies including:
                </p>
                <ul className={`list-disc list-inside text-sm sm:text-base leading-relaxed space-y-2 ml-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>Screen readers (JAWS, NVDA, VoiceOver, etc.)</li>
                  <li>Voice recognition software</li>
                  <li>Screen magnification software</li>
                  <li>Alternative keyboards and mouse devices</li>
                </ul>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 
                  className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Ongoing Efforts
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  We are continuously working to improve accessibility through:
                </p>
                <ul className={`list-disc list-inside text-sm sm:text-base leading-relaxed space-y-2 ml-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>Regular accessibility audits and testing</li>
                  <li>User feedback integration</li>
                  <li>Staff training on accessibility best practices</li>
                  <li>Staying current with accessibility standards and technology</li>
                  <li>Third-party accessibility assessments</li>
                </ul>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 
                  className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Known Limitations
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Despite our best efforts, there may be some areas of our platform that are not fully accessible. We are actively working to address these limitations and improve the overall accessibility of our services.
                </p>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 
                  className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Feedback and Contact
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  We welcome your feedback on the accessibility of our platform. Please let us know if you encounter accessibility barriers:
                </p>
                <ul className={`list-disc list-inside text-sm sm:text-base leading-relaxed space-y-2 ml-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>Contact us through our support page</li>
                  <li>Describe the specific issue you encountered</li>
                  <li>Include information about your assistive technology if applicable</li>
                  <li>We aim to respond to accessibility feedback within 5 business days</li>
                </ul>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 
                  className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                >
                  Alternative Access
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  If you are unable to access any content or feature on our platform due to a disability, we are committed to providing you with alternative access. Please contact our support team, and we will work with you to provide the information or service through an alternative communication method.
                </p>
              </section>
            </div>

            <div className={`mt-8 sm:mt-12 p-4 sm:p-6 rounded-lg text-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`text-sm sm:text-base mb-4 sm:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Have accessibility concerns or suggestions?
              </p>
              <a
                href={`/${window.location.pathname.split('/')[1]}/contact`}
                className="inline-block px-6 py-3 sm:px-8 sm:py-4 bg-[var(--cc-action)] text-[var(--cc-action-text)] font-semibold rounded-lg hover:bg-[var(--cc-action-hover)] transition-colors duration-200 text-sm sm:text-base"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccessibilityPolicy;